import React, { useState, useMemo } from 'react';
import { App as AntdApp, Form, Radio, Button, Alert, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { NumericInput } from '../common';
import { useAddOnPurchase, calculateAddOnPrice, normalizeAddOnQuantity } from '../../hooks/useAddOnPurchase';
import { getAddOnConfig, type AddOnType, calculatePriceWithTax } from '@shared/config/subscription-addons';
import { trpc } from '../../lib/trpc';
import { AddonAddConfirmDialog, type AddonPreview } from './AddonAddConfirmDialog';

const { Text } = Typography;
const formatYen = (value: number) => `${value.toLocaleString()}円`;
type SelectableAddOnType = Exclude<AddOnType, 'prediction'>;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : '想定外のエラーが発生しました';

export const AddonAddForm: React.FC = () => {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const { addAddOn, isAddingAddOn } = useAddOnPurchase();
  const [selectedType, setSelectedType] = useState<SelectableAddOnType>('equipment');
  const [quantity, setQuantity] = useState<number>(50);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<{ type: SelectableAddOnType; quantity: number } | null>(null);

  const { data: previewData } = trpc.subscriptionAddon.previewAddOn.useQuery(
    {
      type: pendingValues?.type || 'equipment',
      quantity: pendingValues?.quantity || 50,
    },
    {
      enabled: confirmDialogOpen && !!pendingValues,
    }
  );

  const addonConfig = useMemo(() => getAddOnConfig(selectedType), [selectedType]);
  const unit = addonConfig.unit;
  const unitPrice = addonConfig.unitPrice;
  const unitLabel = addonConfig.unitLabel ?? '台';
  const normalizedQuantity = normalizeAddOnQuantity(selectedType, quantity);
  const lotCount = Math.max(1, Math.ceil(quantity / unit));
  const totalPrice = calculateAddOnPrice(selectedType, quantity);
  const totalPriceWithTax = calculatePriceWithTax(totalPrice);

  const handleSubmit = async (values: { type: SelectableAddOnType; quantity: number }) => {
    setPendingValues(values);
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingValues) return;

    try {
      await addAddOn(
        {
          type: pendingValues.type,
          quantity: pendingValues.quantity,
        },
        {
          onSuccess: () => {
            message.success('追加オプションを追加しました');
            form.resetFields();
            setSelectedType('equipment');
            setQuantity(50);
            setConfirmDialogOpen(false);
            setPendingValues(null);
          },
          onError: (error) => {
            message.error(`追加オプションの追加に失敗しました: ${error.message}`);
            setConfirmDialogOpen(false);
            setPendingValues(null);
          },
        }
      );
    } catch (error) {
      message.error(`エラーが発生しました: ${getErrorMessage(error)}`);
      setConfirmDialogOpen(false);
      setPendingValues(null);
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setPendingValues(null);
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ type: 'equipment', quantity: 50 }}
      >
        <Form.Item
          name="type"
          label="追加オプションタイプ"
          rules={[{ required: true, message: '追加オプションタイプを選択してください' }]}
        >
          <Radio.Group
            onChange={(e) => {
              const newType = e.target.value as SelectableAddOnType;
              setSelectedType(newType);
              form.setFieldValue('type', newType);
              const newConfig = getAddOnConfig(newType);
              setQuantity(newConfig.unit);
              form.setFieldValue('quantity', newConfig.unit);
            }}
          >
            <Space direction="vertical">
              <Radio value="equipment">
                <strong>設備数追加</strong> (50台単位: {formatYen(20_000)}/月{' '}
                <Text type="secondary" style={{ color: '#999' }}>(税込{formatYen(22_000)}/月)</Text>)
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  設備登録枠を50台ずつまとめて追加
                </Text>
              </Radio>
              <Radio value="storage">
                <strong>ストレージ容量追加</strong> (50GB単位: {formatYen(5_000)}/月{' '}
                <Text type="secondary" style={{ color: '#999' }}>(税込{formatYen(5_500)}/月)</Text>)
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  データストレージ容量を50GBずつ追加
                </Text>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="quantity"
          label={`追加数量 (${unit}${unitLabel}単位)`}
          rules={[
            { required: true, message: '数量を入力してください' },
            { type: 'number', min: unit, message: `最低${unit}${unitLabel}から追加できます` },
          ]}
        >
          <NumericInput
            min={unit}
            step={unit}
            style={{ width: 200 }}
            addonAfter={unitLabel}
            onChange={(val) => {
              const safeValue = typeof val === 'number' ? val : unit;
              setQuantity(safeValue);
              form.setFieldValue('quantity', safeValue);
            }}
          />
        </Form.Item>

        <Alert
          type="info"
          message={
            <div>
              <div>
                <strong>追加数量:</strong> {normalizedQuantity}
                {unitLabel}（{lotCount}ロット）
              </div>
              <div style={{ marginTop: 8, fontSize: 16 }}>
                <strong>月額料金:</strong>{' '}
                <span style={{ fontSize: 20 }}>{formatYen(totalPrice)}/月</span>{' '}
                <Text type="secondary" style={{ color: '#999' }}>
                  (税込{formatYen(totalPriceWithTax)}/月)
                </Text>
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                {lotCount}ロット × {formatYen(unitPrice)}
              </div>
            </div>
          }
          style={{ marginBottom: 16 }}
        />

        <Alert
          type="warning"
          message="追加オプション追加時の注意"
          description={
            <>
              <div>追加オプションの追加後、反映までに数分程度かかる場合があります。追加完了後、しばらく経ってからご利用状況をご確認ください。</div>
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                ※ 追加オプションをキャンセルされた場合、日割りでの返金はございません。
              </div>
            </>
          }
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<PlusOutlined />}
            loading={isAddingAddOn}
          >
            追加オプションを追加
          </Button>
        </Form.Item>

        <AddonAddConfirmDialog
          open={confirmDialogOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          preview={
            previewData
              ? ({
                ...previewData,
                addonType: previewData.addonType ?? pendingValues?.type ?? selectedType,
              } as AddonPreview)
              : null
          }
          loading={isAddingAddOn}
        />
      </Form>
    </>
  );
};

export default AddonAddForm;

